import { useState, useEffect, FormEvent } from 'react'
import FormField from '../../../components/common/FormField'
import FileUpload from '../../../components/common/FileUpload'
import Button from '../../../components/common/Button'

interface Document {
  id?: number
  title: string
  description: string
  media_id?: number
  category: string
  position: number
  featured: boolean
  status: 'draft' | 'published'
}

interface DocumentFormProps {
  document?: Document | null
  onSubmit: (doc: Omit<Document, 'id'>) => void
  onCancel: () => void
}

const CATEGORIES = [
  { value: 'flyers', label: 'Flyers' },
  { value: 'programme', label: 'Programme' },
  { value: 'communiques', label: 'Communiqués' },
  { value: 'autres', label: 'Autres' },
]

function DocumentForm({ document, onSubmit, onCancel }: DocumentFormProps) {
  const [formData, setFormData] = useState<Omit<Document, 'id'>>({
    title: '',
    description: '',
    media_id: undefined,
    category: 'flyers',
    position: 0,
    featured: false,
    status: 'draft',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [filePreview, setFilePreview] = useState<{ name: string; type: string } | null>(null)

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title,
        description: document.description,
        media_id: document.media_id,
        category: document.category,
        position: document.position,
        featured: document.featured,
        status: document.status,
      })
    }
  }, [document])

  const handleFileUpload = (fileData: any) => {
    if (fileData && fileData.id) {
      setFormData({
        ...formData,
        media_id: fileData.id,
      })
      // Set file preview for display
      setFilePreview({
        name: fileData.file_name || fileData.filename || 'Fichier téléchargé',
        type: fileData.mime_type || fileData.type || '',
      })
      setErrors({ ...errors, file: '' })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis'
    }

    if (!formData.media_id && !document) {
      newErrors.file = 'Un fichier est requis'
    }

    if (formData.position < 0) {
      newErrors.position = 'La position doit être positive'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (validate()) {
      onSubmit(formData)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="document-form">
      <FormField
        label="Titre"
        id="title"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        error={errors.title}
        required
      />

      <div className="form-field">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          placeholder="Description du document..."
        />
      </div>

      <div className="form-field">
        <label>
          Fichier
          {!document && <span className="required">*</span>}
        </label>
        <FileUpload
          onUpload={handleFileUpload}
          accept=".pdf,.doc,.docx"
          label={filePreview?.name || 'Choisir un fichier'}
        />
        {filePreview && (
          <div className="file-preview">
            <span className="file-name">{filePreview.name}</span>
            <span className="file-type">{filePreview.type}</span>
          </div>
        )}
        {errors.file && <span className="field-error">{errors.file}</span>}
      </div>

      <div className="form-field">
        <label htmlFor="category">
          Catégorie
          <span className="required">*</span>
        </label>
        <select
          id="category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <FormField
        label="Position"
        id="position"
        type="number"
        value={formData.position}
        onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
        error={errors.position}
        min="0"
      />

      <div className="form-field checkbox-field">
        <label>
          <input
            type="checkbox"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
          />
          <span>Mettre en avant ce document</span>
        </label>
      </div>

      <div className="form-field">
        <label htmlFor="status">Statut</label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
        >
          <option value="draft">Brouillon</option>
          <option value="published">Publié</option>
        </select>
      </div>

      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="primary">
          {document ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  )
}

export default DocumentForm
